import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'dark' | 'light';
export type ChartColorScheme = 'classic' | 'vibrant' | 'monochrome';
export type FontSizeMode = 'small' | 'medium' | 'large';
export type DensityMode = 'compact' | 'comfortable' | 'spacious';

export interface AppearanceSettings {
  themeMode: ThemeMode;
  chartColorScheme: ChartColorScheme;
  fontSize: FontSizeMode;
  density: DensityMode;
  showAnimations: boolean;
  showGridLines: boolean;
  compactTables: boolean;
}

const APPEARANCE_STORAGE_KEY = 'zippy_appearance_settings';

const defaultAppearanceSettings: AppearanceSettings = {
  themeMode: 'dark',
  chartColorScheme: 'classic',
  fontSize: 'medium',
  density: 'comfortable',
  showAnimations: true,
  showGridLines: true,
  compactTables: false,
};

interface AppearanceContextType {
  appearance: AppearanceSettings;
  updateAppearance: (nextAppearance: AppearanceSettings) => void;
  patchAppearance: (partial: Partial<AppearanceSettings>) => void;
}

const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

const loadAppearanceSettings = (): AppearanceSettings => {
  if (typeof window === 'undefined') {
    return defaultAppearanceSettings;
  }

  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) {
      return defaultAppearanceSettings;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return defaultAppearanceSettings;
    }

    return {
      ...defaultAppearanceSettings,
      ...parsed,
    };
  } catch {
    return defaultAppearanceSettings;
  }
};

export const AppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [appearance, setAppearance] = useState<AppearanceSettings>(loadAppearanceSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
    } catch {
      // Ignore localStorage write failures
    }

    document.documentElement.setAttribute('data-theme-mode', appearance.themeMode);
    document.documentElement.setAttribute('data-font-size', appearance.fontSize);
    document.documentElement.setAttribute('data-animations', appearance.showAnimations ? 'on' : 'off');
    document.documentElement.setAttribute('data-chart-grid-lines', appearance.showGridLines ? 'on' : 'off');

    const densityMode: DensityMode = appearance.compactTables ? 'compact' : appearance.density;
    document.documentElement.setAttribute('data-table-density', densityMode);
  }, [appearance]);

  const updateAppearance = useCallback((nextAppearance: AppearanceSettings) => {
    setAppearance(nextAppearance);
  }, []);

  const patchAppearance = useCallback((partial: Partial<AppearanceSettings>) => {
    setAppearance((prev) => ({
      ...prev,
      ...partial,
    }));
  }, []);

  const value = useMemo<AppearanceContextType>(() => ({
    appearance,
    updateAppearance,
    patchAppearance,
  }), [appearance, patchAppearance, updateAppearance]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export const useAppearance = (): AppearanceContextType => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};
